import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LANGS = [["en", "English"], ["zh", "中文 (Mandarin)"]];
const cls = "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#D97706]";

function FieldEditor({ name, value, onChange }) {
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return <textarea rows={Math.max(3, value.length)} value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} className={cls} data-testid={`tr-field-${name}`} />;
  }
  if (typeof value === "object" && value !== null) {
    return <textarea rows={6} value={JSON.stringify(value, null, 2)} onChange={(e) => { try { onChange(JSON.parse(e.target.value)); } catch { /* wait for valid JSON */ } }} className={`${cls} font-mono text-xs`} data-testid={`tr-field-${name}`} />;
  }
  const long = String(value || "").length > 80;
  return long ? <textarea rows={String(value).length > 400 ? 10 : 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`tr-field-${name}`} />
    : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`tr-field-${name}`} />;
}

export function TranslateButton({ onClick, testid }) {
  return <button type="button" onClick={onClick} title="Terjemahan" className="border p-2 text-[#0284C7] hover:bg-sky-50" data-testid={testid}><Languages size={14} /></button>;
}

export default function TranslateDialog({ open, onClose, kind, item, fields, onSaved }) {
  const [i18n, setI18n] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setI18n(item?.i18n || {}); }, [item]);
  const auto = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/translate", { kind, id: item?.id, langs: ["en", "zh"] });
      setI18n(data.i18n);
      toast.success("Terjemahan AI selesai. Periksa & simpan bila perlu diedit.");
      onSaved?.(data.i18n);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try {
      if (kind === "profile") onSaved?.(i18n, true);
      else {
        await api.put(`/admin/content/${kind}/${item.id}`, { i18n });
        onSaved?.(i18n, true);
      }
      toast.success("Terjemahan disimpan");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (lang, k, v) => setI18n({ ...i18n, [lang]: { ...(i18n[lang] || {}), [k]: v } });
  const present = fields.filter((k) => item && item[k] !== undefined && item[k] !== "" && !(Array.isArray(item[k]) && !item[k].length));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-none" data-testid="translate-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Languages size={18} /> Terjemahan Konten</DialogTitle></DialogHeader>
        <button onClick={auto} disabled={busy} className="inline-flex items-center justify-center gap-2 bg-[#D97706] py-3 text-sm font-bold text-white hover:bg-[#B45309] disabled:opacity-60" data-testid="translate-auto-button">
          <Sparkles size={16} /> {busy ? "Menerjemahkan dengan AI... (±20 detik)" : "Terjemahkan Otomatis (AI) ke English & 中文"}
        </button>
        <Tabs defaultValue="en">
          <TabsList className="w-full rounded-none">{LANGS.map(([c, l]) => <TabsTrigger key={c} value={c} className="flex-1 rounded-none" data-testid={`translate-tab-${c}`}>{l}{i18n[c] ? " ✓" : ""}</TabsTrigger>)}</TabsList>
          {LANGS.map(([c]) => (
            <TabsContent key={c} value={c} className="space-y-3">
              {!i18n[c] && <p className="text-xs text-slate-500">Belum ada terjemahan. Klik tombol AI di atas atau isi manual — kolom kosong akan memakai teks Indonesia.</p>}
              {present.map((k) => (
                <label key={k} className="block text-xs font-semibold text-slate-600">
                  {k} <span className="font-normal text-slate-400">— ID: {typeof item[k] === "string" ? item[k].slice(0, 70) : "…"}</span>
                  <FieldEditor name={`${c}-${k}`} value={i18n[c]?.[k] ?? (Array.isArray(item[k]) ? [] : typeof item[k] === "object" ? item[k] : "")} onChange={(v) => set(c, k, v)} />
                </label>
              ))}
            </TabsContent>
          ))}
        </Tabs>
        <button onClick={save} className="bg-[#0F2B48] py-3 text-sm font-bold text-white hover:bg-[#D97706]" data-testid="translate-save-button">Simpan Terjemahan</button>
      </DialogContent>
    </Dialog>
  );
}
